import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { CheckCircle2, Instagram, Loader2, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLE_LABEL } from "@/pages/Companies";
import { errMsg, useApi } from "@/lib/api";

export const INSTAGRAM_URL = "https://www.instagram.com/terraloop.cl/";
const EMPTY = { empresa: "", contacto: "", correo: "", movil: "", mensaje: "", sitio: "" };
const OTHER = "__otra__";
const words = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

const F = ({ label, children, className = "" }) => <div className={`space-y-2 ${className}`}><Label className="text-slate-700">{label}</Label>{children}</div>;

const CompanyField = ({ f, setF, companies }) => {
  const [pick, setPick] = useState("");
  if (!companies?.length) {
    return <Input required className="h-11" value={f.empresa} onChange={(e) => setF({ ...f, empresa: e.target.value })} data-testid="contact-company-input" />;
  }
  const onPick = (v) => {
    setPick(v);
    setF({ ...f, empresa: v === OTHER ? "" : v });
  };
  return (
    <div className="space-y-2">
      <Select value={pick} onValueChange={onPick}>
        <SelectTrigger className="h-11" data-testid="contact-company-select"><SelectValue placeholder="Elige una de tus empresas" /></SelectTrigger>
        <SelectContent>
          {companies.map((c) => <SelectItem key={c.id} value={c.name} data-testid={`contact-company-option-${c.id}`}>{c.name} · {ROLE_LABEL[c.role]}</SelectItem>)}
          <SelectItem value={OTHER} data-testid="contact-company-option-other">Otra empresa</SelectItem>
        </SelectContent>
      </Select>
      {pick === OTHER && <Input required className="h-11" placeholder="Nombre de la empresa" value={f.empresa} onChange={(e) => setF({ ...f, empresa: e.target.value })} data-testid="contact-company-input" />}
    </div>
  );
};

const useMyCompanies = () => {
  const { isSignedIn } = useAuth();
  const api = useApi();
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!isSignedIn) { setList([]); return; }
    api("get", "/me/documentos-habilitadores").then((r) => {
      const seen = new Set();
      setList(r.data.filter((c) => c.resumen?.razon_social).map((c) => ({ id: c.id, role: c.role, name: c.resumen.razon_social }))
        .filter((c) => !seen.has(`${c.name}${c.role}`) && seen.add(`${c.name}${c.role}`)));
    }).catch(() => setList([]));
  }, [api, isSignedIn]);
  return list;
};

export default function Contact() {
  const api = useApi();
  const companies = useMyCompanies();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [folio, setFolio] = useState(null);
  const n = words(f.mensaje);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.empresa.trim()) return toast.error("Indica el nombre de la empresa");
    if (n > 500) return toast.error("El mensaje admite un máximo de 500 palabras");
    setBusy(true);
    try {
      const { data } = await api("post", "/contacto", f);
      setFolio(data.folio || "OK");
      setF(EMPTY);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-[1fr_1.4fr] gap-12" data-testid="contact-page">
      <div className="space-y-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a]">Lo que somos · Contáctanos</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#05130e]">Conversemos</h1>
        <p className="text-base md:text-lg text-slate-600">Cuéntanos qué materiales generas, qué necesitas valorizar o cómo quieres sumarte a la simbiosis industrial. Respondemos desde contacto@terraloop.cl.</p>
        <div className="space-y-3 text-sm">
          <a href="mailto:contacto@terraloop.cl" className="flex items-center gap-3 text-[#05130e] hover:text-[#00915f]" data-testid="contact-mail-link"><Mail className="w-5 h-5 text-[#00b37a]" />contacto@terraloop.cl</a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[#05130e] hover:text-[#00915f]" data-testid="contact-instagram-link"><Instagram className="w-5 h-5 text-[#00b37a]" />@terraloop.cl</a>
        </div>
      </div>
      {folio ? (
        <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center space-y-4" data-testid="contact-success">
          <CheckCircle2 className="w-12 h-12 text-[#00b37a] mx-auto" />
          <h2 className="text-2xl font-bold text-[#05130e]">¡Mensaje enviado!</h2>
          <p className="text-slate-600">Gracias por escribirnos. Te responderemos a la brevedad. Folio <span className="font-mono font-semibold" data-testid="contact-folio">{folio}</span></p>
          <Button variant="outline" className="rounded-full" onClick={() => setFolio(null)} data-testid="contact-new-btn">Enviar otro mensaje</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
          <F label="Nombre empresa" className="sm:col-span-2"><CompanyField f={f} setF={setF} companies={companies} /></F>
          <F label="Contacto empresa"><Input required minLength={2} className="h-11" placeholder="Nombre y apellido" value={f.contacto} onChange={set("contacto")} data-testid="contact-person-input" /></F>
          <F label="Correo"><Input required type="email" className="h-11" value={f.correo} onChange={set("correo")} data-testid="contact-email-input" /></F>
          <F label="Móvil de contacto"><Input required type="tel" className="h-11" placeholder="+56 9 1234 5678" value={f.movil} onChange={set("movil")} data-testid="contact-phone-input" /></F>
          <input tabIndex={-1} autoComplete="off" value={f.sitio} onChange={set("sitio")} className="hidden" aria-hidden="true" />
          <F label="Mensaje" className="sm:col-span-2">
            <Textarea required rows={8} value={f.mensaje} onChange={set("mensaje")} className="rounded-xl" data-testid="contact-message-input" />
            <p className={`text-xs text-right ${n > 500 ? "text-red-600" : "text-slate-400"}`} data-testid="contact-word-count">{n} / 500 palabras</p>
          </F>
          <div className="sm:col-span-2 flex justify-end">
            <Button disabled={busy || n > 500} className="rounded-full px-8 h-12 font-semibold" data-testid="contact-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Enviar mensaje</Button>
          </div>
        </form>
      )}
    </main>
  );
}
